/**
 * Project Documents & Communications tab (Patch E26).
 *
 * Unified feed of documents and communications anchored to this project (or
 * any of its sub-entities). Filter strip on top, dense list below, two CTAs
 * (Upload document / Log communication) top-right.
 *
 * Clicking a row opens the detail drawer. The list invalidates after any
 * upload, log, edit, or delete.
 */
import { useMemo, useState } from 'react';
import { FileText, Phone, Calendar, Mail, MessageSquare, Send, ArrowDown, NotebookPen, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/form-field';
import { Select } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { formatDate } from '@procurement/shared';
import {
  useAttachmentsList,
  type Attachment,
  type AttachmentKind,
  type CommType,
  type DocumentType,
} from '@/lib/attachments-queries';
import { useProjectItems, useBankGuarantees } from '@/lib/pipeline-queries';
import { AttachmentDetailDrawer } from '@/components/attachments/detail-drawer';
import { UploadDocumentModal } from '@/components/attachments/upload-document-modal';
import { LogCommunicationModal } from '@/components/attachments/log-communication-modal';

interface Props {
  projectId: string;
  canEdit: boolean;
}

type KindFilter = 'ALL' | AttachmentKind;
type AnchorScope = 'ALL' | 'PROJECT_LEVEL' | 'PART' | 'BATCH' | 'BG' | 'INSPECTION';

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  DFA: 'DFA',
  SCHEDULE_OF_STORES: 'Schedule of Stores',
  PO: 'PO',
  SUPPLIER_INVOICE: 'Supplier invoice',
  CLIENT_INVOICE: 'Client invoice',
  BG_INSTRUMENT: 'BG instrument',
  INSPECTION_REPORT: 'Inspection report',
  LETTER: 'Letter',
  EMAIL_SCAN: 'Email',
  CHEQUE_COPY: 'Cheque',
  CERTIFICATE: 'Certificate',
  DRAWING: 'Drawing',
  AGREEMENT: 'Agreement',
  I_NOTE: 'I-Note',
  CRV: 'CRV',
  OTHER: 'Other',
};

export function ProjectDocumentsTab({ projectId, canEdit }: Props) {
  const [kind, setKind] = useState<KindFilter>('ALL');
  const [anchorScope, setAnchorScope] = useState<AnchorScope>('ALL');
  const [search, setSearch] = useState('');
  const [docType, setDocType] = useState<DocumentType | ''>('');
  const [openDrawerId, setOpenDrawerId] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);

  const filters = useMemo(
    () => ({
      projectId,
      kind: kind === 'ALL' ? undefined : kind,
      projectLevelOnly: anchorScope === 'PROJECT_LEVEL' ? true : undefined,
      documentType: docType || undefined,
      search: search.trim() || undefined,
      pageSize: 100,
    }),
    [projectId, kind, anchorScope, docType, search],
  );

  const list = useAttachmentsList(filters);

  // Patch 3 — used by the empty state to distinguish "nothing matches" from
  // "nothing logged yet". Keep this in sync with the filter controls above.
  const hasActiveFilter =
    kind !== 'ALL' || anchorScope !== 'ALL' || docType !== '' || search.trim() !== '';

  // Load project entities for the AnchorPicker (parts, batches, BGs, inspections).
  const items = useProjectItems(projectId);
  const bgs = useBankGuarantees(projectId);

  const parts = useMemo(
    () =>
      (items.data ?? [])
        .filter((i) => i.itemType === 'PART')
        .map((i) => ({ id: i.id, label: `${i.srNumber ?? '—'} · ${i.name}` })),
    [items.data],
  );
  const batches = useMemo(() => {
    // For each PART, gather active batches. The pipeline-queries layer
    // exposes batches under a separate hook; for the AnchorPicker we just
    // need a flat list. We'll lazily populate batches per part later — for
    // now this tab's picker accepts an empty batches list and the user can
    // still anchor by part. (Inline badges on batch rows in batch-surface
    // pre-fill batch directly anyway.)
    return [] as { id: string; label: string }[];
  }, []);
  const bankGuaranteeOptions = useMemo(
    () =>
      (bgs.data ?? []).map((g, idx) => ({
        id: g.id,
        label: `BG #${idx + 1} · ${g.status}`,
      })),
    [bgs.data],
  );
  const inspectionOptions = useMemo(() => {
    // Same constraint as batches — populated lazily by the inspection-event drawer
    return [] as { id: string; label: string }[];
  }, []);

  const entities = { parts, batches, bankGuarantees: bankGuaranteeOptions, inspectionEvents: inspectionOptions };

  const rows = list.data?.items ?? [];

  return (
    <>
      {/* Header / actions */}
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-primary">Documents & communications</h2>
          {list.data ? (
            <p className="text-xs text-secondary">
              {list.data.total} {list.data.total === 1 ? 'record' : 'records'}
            </p>
          ) : null}
        </div>
        {canEdit ? (
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setUploadOpen(true)}>
              <Paperclip className="h-3.5 w-3.5" strokeWidth={1.5} />
              Upload document
            </Button>
            <Button onClick={() => setLogOpen(true)}>
              <Phone className="h-3.5 w-3.5" strokeWidth={1.5} />
              Log communication
            </Button>
          </div>
        ) : null}
      </div>

      {/* Filter strip */}
      <Card>
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">
              Kind
            </span>
            {(['ALL', 'DOCUMENT', 'COMMUNICATION'] as KindFilter[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={
                  'rounded-sm border px-2 py-0.5 ' +
                  (kind === k
                    ? 'border-accent bg-accent/5 text-accent-strong'
                    : 'border-border-default text-secondary hover:border-border-strong')
                }
              >
                {k === 'ALL' ? 'All' : k === 'DOCUMENT' ? 'Documents' : 'Communications'}
              </button>
            ))}
            <span className="ml-3 text-[11px] font-semibold uppercase tracking-wide text-tertiary">
              Anchor
            </span>
            {(['ALL', 'PROJECT_LEVEL'] as AnchorScope[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setAnchorScope(s)}
                className={
                  'rounded-sm border px-2 py-0.5 ' +
                  (anchorScope === s
                    ? 'border-accent bg-accent/5 text-accent-strong'
                    : 'border-border-default text-secondary hover:border-border-strong')
                }
              >
                {s === 'ALL' ? 'All' : 'Project-level only'}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Search subject, notes, name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="min-w-[240px] flex-1"
            />
            {kind !== 'COMMUNICATION' ? (
              <Select
                value={docType}
                onChange={(e) => setDocType((e.target.value || '') as DocumentType | '')}
                className="w-44"
              >
                <option value="">All doc types</option>
                {(Object.keys(DOC_TYPE_LABELS) as DocumentType[]).map((t) => (
                  <option key={t} value={t}>
                    {DOC_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            ) : null}
          </div>
        </div>
      </Card>

      {/* Feed */}
      <div className="mt-3">
        {list.isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Spinner className="h-5 w-5 text-tertiary" />
          </div>
        ) : list.isError ? (
          <EmptyState
            title="Could not load attachments"
            body="The server didn't return a response. This is usually a transient issue."
            action={
              <button
                type="button"
                onClick={() => void list.refetch()}
                className="rounded-sm border border-border-default px-3 py-1 text-xs text-secondary hover:border-border-strong hover:text-primary"
              >
                Try again
              </button>
            }
          />
        ) : rows.length === 0 ? (
          hasActiveFilter ? (
            <EmptyState
              title="No documents match your filters"
              body="Adjust the filters above or clear them to see more results."
              action={
                <button
                  type="button"
                  onClick={() => {
                    setKind('ALL');
                    setAnchorScope('ALL');
                    setSearch('');
                    setDocType('');
                  }}
                  className="rounded-sm border border-border-default px-3 py-1 text-xs text-secondary hover:border-border-strong hover:text-primary"
                >
                  Clear filters
                </button>
              }
            />
          ) : (
            <EmptyState
              title="No documents or communications yet"
              body="Attach DFA letters, POs, invoices, and inspection reports here. Log phone calls and meetings to keep a contact history."
              action={
                canEdit ? (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setLogOpen(true)}
                      className="rounded-sm border border-border-default px-3 py-1 text-xs text-secondary hover:border-border-strong hover:text-primary"
                    >
                      Log communication
                    </button>
                    <button
                      type="button"
                      onClick={() => setUploadOpen(true)}
                      className="rounded-sm border border-accent bg-accent/5 px-3 py-1 text-xs text-accent-strong hover:bg-accent/10"
                    >
                      Upload document
                    </button>
                  </div>
                ) : null
              }
            />
          )
        ) : (
          <ul className="divide-y divide-border-default rounded-sm border border-border-default bg-surface">
            {rows.map((r) => (
              <li key={r.id}>
                <AttachmentRow
                  data={r}
                  onClick={() => setOpenDrawerId(r.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Drawer + modals */}
      <AttachmentDetailDrawer
        attachmentId={openDrawerId}
        onClose={() => setOpenDrawerId(null)}
      />
      {canEdit ? (
        <>
          <UploadDocumentModal
            open={uploadOpen}
            onClose={() => setUploadOpen(false)}
            projectId={projectId}
            entities={entities}
          />
          <LogCommunicationModal
            open={logOpen}
            onClose={() => setLogOpen(false)}
            projectId={projectId}
          />
        </>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function AttachmentRow({ data, onClick }: { data: Attachment; onClick: () => void }) {
  const isDoc = data.kind === 'DOCUMENT';
  const Icon = isDoc
    ? FileText
    : iconForCommType(data.commType);

  const anchorLabel = computeAnchorLabel(data);
  const title = isDoc ? data.displayName : data.subject;
  const subtitle = isDoc
    ? data.documentType
      ? DOC_TYPE_LABELS[data.documentType]
      : null
    : data.notes
      ? data.notes.slice(0, 100)
      : null;
  const dateStr = data.occurredAt
    ? formatDate(data.occurredAt.slice(0, 10))
    : data.documentDate
      ? formatDate(data.documentDate)
      : formatDate(data.createdAt.slice(0, 10));

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-3 px-3 py-2 text-left transition-colors hover:bg-subtle"
    >
      <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-base text-secondary">
        <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm text-primary">{title}</span>
          <span className="shrink-0 text-xs text-secondary">{dateStr}</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-secondary">
          {subtitle ? <span className="truncate">{subtitle}</span> : null}
          {anchorLabel ? (
            <span className="rounded-sm bg-base px-1.5 py-0.5 text-[10px] text-secondary">
              {anchorLabel}
            </span>
          ) : null}
          {data.tags.length > 0 ? (
            <span className="flex flex-wrap gap-1">
              {data.tags.slice(0, 3).map((t) => (
                <span key={t} className="text-[10px] text-accent-strong">
                  #{t}
                </span>
              ))}
              {data.tags.length > 3 ? (
                <span className="text-[10px] text-tertiary">+{data.tags.length - 3}</span>
              ) : null}
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}

function iconForCommType(t: CommType | null) {
  switch (t) {
    case 'PHONE_CALL':
      return Phone;
    case 'MEETING':
      return Calendar;
    case 'LETTER_RECEIVED':
      return ArrowDown;
    case 'LETTER_SENT':
      return Send;
    case 'EMAIL':
      return Mail;
    case 'WHATSAPP':
    case 'SMS':
      return MessageSquare;
    case 'INTERNAL_NOTE':
    default:
      return NotebookPen;
  }
}

function computeAnchorLabel(data: Attachment): string | null {
  if (data.bankGuaranteeId) return 'BG';
  if (data.inspectionEventId) return 'Inspection';
  if (data.orderBatchId) return 'Batch';
  if (data.projectItemId) return 'Part';
  return 'Project-level';
}
